import React, {useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {Plus, X} from '../../icons';
import {PlatformLogo} from './PlatformLogo';
import {NEW_CHANNELS, benefitKeyOf, kindKeyOf} from './platformsModel';
import {platformName} from './platformsText';

const NS = 'agency_v2.platforms';
const RAW = {escapeValue: false};

/** Le da el foco al nodo cuando se pide, apenas está montado, y avisa que ya lo tomó. */
function useFocusWhen(focusNow, ref, onFocused) {
    useEffect(() => {
        if (!focusNow || !ref.current) return;
        ref.current.focus();
        if (onFocused) onFocused();
    }, [focusNow, ref, onFocused]);
}

/**
 * «Ver las N que ocultaste»: devuelve las tarjetas que el usuario sacó con la X. Va al pie del bloque
 * mientras quede alguna tarjeta, y suelto al pie de la pantalla cuando las ocultó todas — si se fuera
 * con el bloque, no habría forma de recuperarlas.
 */
export function ShowHiddenLink({count, onShowHidden, standalone = false, focusNow = false, onFocused}) {
    const {t} = useTranslation();
    const buttonRef = useRef(null);
    // Ocultar la ÚLTIMA tarjeta desmonta el bloque que tenía el foco: el foco viene acá, que es lo que
    // aparece en su lugar, en vez de caer a <body> (QA-plat-03).
    useFocusWhen(focusNow, buttonRef, onFocused);
    if (!count) return null;
    return (
        <p className={`apl-show-hidden${standalone ? ' -standalone' : ''}`}>
            <button type="button" className="apl-link" onClick={onShowHidden} data-testid="apl-show-hidden"
                    ref={buttonRef}>
                {t(`${NS}.discover_show_hidden`, {count})}
            </button>
        </p>
    );
}

/**
 * «Dashbo también se conecta con estas plataformas» (D6): las habilitadas que la agencia todavía no
 * conectó, cada una con lo que suma y su «Conectar». La X las oculta para este usuario en esta
 * agencia (D7). Sin tarjetas para mostrar, el bloque no se dibuja.
 */
export function DiscoverBlock({platforms, hiddenCount, onConnect, onHide, onShowHidden, focusNow = false, onFocused}) {
    const {t} = useTranslation();
    const sectionRef = useRef(null);
    // «Ver las N que ocultaste» desaparece con el clic que devuelve las tarjetas: el foco vuelve al
    // bloque, que es donde aparecieron.
    useFocusWhen(focusNow, sectionRef, onFocused);
    if (!platforms.length) return null;

    const hide = (channel) => () => {
        onHide(channel);
        // La tarjeta se va con el botón que tenía el foco: el foco vuelve al bloque, no al <body>.
        if (sectionRef.current) sectionRef.current.focus();
    };

    return (
        <section className="apl-discover" id="apl-discover" aria-labelledby="apl-discover-title" tabIndex={-1}
                 ref={sectionRef} data-testid="apl-discover">
            <div className="apl-discover__head">
                <span className="apl-discover__icon" aria-hidden="true"><Plus size={20}/></span>
                <div>
                    <h2 className="apl-discover__title" id="apl-discover-title">{t(`${NS}.discover_title`)}</h2>
                    <p className="apl-discover__text">{t(`${NS}.discover_text`)}</p>
                </div>
            </div>
            <ul className="apl-tiles">
                {platforms.map(({channel}) => {
                    const name = platformName(channel);
                    const nameId = `apl-tile-${channel}`;
                    return (
                        <li key={channel} className="apl-tile" data-testid={`apl-tile-${channel}`}>
                            <button type="button" className="apl-tile__hide" aria-label={t(`${NS}.discover_hide`)}
                                    aria-describedby={nameId} onClick={hide(channel)}>
                                <X size={16} aria-hidden="true"/>
                            </button>
                            <div className="apl-tile__top">
                                <PlatformLogo channel={channel} size={26} className="-lg"/>
                                {/* «Nuevo» va con el rótulo y no con el nombre: al lado del nombre no entraba en una
                                    tarjeta de 200 a 260 px y partía la cabecera en tres o cuatro renglones (QA-VIS-maf-R01). */}
                                <span className="apl-tile__name">
                                    <span className="apl-tile__title" id={nameId}>{name}</span>
                                    <span className="apl-tile__kind">
                                        {t(kindKeyOf(channel))}
                                        {NEW_CHANNELS.includes(channel) && <span className="apl-tile__new">{t(`${NS}.discover_new`)}</span>}
                                    </span>
                                </span>
                            </div>
                            <p className="apl-tile__benefit">{t(benefitKeyOf(channel))}</p>
                            <button type="button" className="apl-btn -sm" onClick={() => onConnect(channel)}>
                                {t(`${NS}.discover_connect`, {platform: name, interpolation: RAW})}
                            </button>
                        </li>
                    );
                })}
            </ul>
            <ShowHiddenLink count={hiddenCount} onShowHidden={onShowHidden}/>
        </section>
    );
}

export default DiscoverBlock;
